/**
 * AuthProvider — central auth state for the React tree.
 *
 * Responsibilities:
 *   1. Listen for Firebase auth state changes.
 *   2. ENFORCE the daaily.com domain restriction on the client. (Server-side
 *      enforcement also exists in Firestore rules and Cloud Function checks —
 *      this client check is for UX, not security.)
 *   3. Expose `signIn`, `signOut`, and the current user.
 *
 * Note on the user document: this provider only cares about Firebase Auth.
 * The Firestore /users/{email} doc creation and "is profile complete?" check
 * is handled in step 3 (User profile). For step 1 we only need auth.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { syncImpersonationFromToken } from './impersonation';

/**
 * Mobile browsers + the installed PWA get the REDIRECT flow: popups there are
 * either blocked or complete without the opener ever hearing about it (the
 * session lands in IndexedDB but the page stays on "Sign in" until a manual
 * reload — the "login needs a hard refresh" bug). Desktop keeps the popup
 * (no full-page navigation) with a redirect fallback if the popup is blocked.
 * Redirect is safe because authDomain is now same-origin (lib/firebase.ts).
 */
function preferRedirect(): boolean {
  const standalone =
    window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    (navigator as { standalone?: boolean }).standalone === true;
  const mobileUa = /iphone|ipad|ipod|android/i.test(navigator.userAgent);
  return standalone || mobileUa;
}

const POPUP_FALLBACK_CODES = new Set([
  'auth/popup-blocked',
  'auth/cancelled-popup-request',
  'auth/operation-not-supported-in-this-environment',
]);

// Staff Workspace + the external-collaborator SSO domain. The `hd` picker hint
// (lib/firebase.ts) stays the primary Workspace domain; this list is the actual
// client-side gate (mirrored server-side in verify.ts + the security rules).
const ALLOWED_DOMAINS = [
  (import.meta.env.VITE_AUTH_HOSTED_DOMAIN ?? 'daaily.com').toLowerCase(),
  'collab.daaily.com',
];

/**
 * Max session age — force a fresh Google SSO re-auth after this long, regardless
 * of activity, to bound how long a lost/stolen device keeps access (pairs with
 * the mobile app-lock; see risk assessment R11). `lastSignInTime` = the last real
 * credential sign-in (token auto-refresh does NOT reset it), so this genuinely
 * caps the age of an unattended session. Set the sessionStorage flag so the login
 * page can say why. Fails open if the timestamp is unparseable.
 */
const MAX_SESSION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
export const SESSION_EXPIRED_KEY = 'daaily.sessionExpired';

function sessionAgeMs(user: FirebaseUser): number | null {
  const raw = user.metadata?.lastSignInTime;
  const t = raw ? Date.parse(raw) : NaN;
  return Number.isFinite(t) ? Date.now() - t : null;
}
function sessionExpired(user: FirebaseUser): boolean {
  const age = sessionAgeMs(user);
  return age != null && age > MAX_SESSION_MS;
}

type AuthState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'signed_in'; user: FirebaseUser }
  | { status: 'wrong_domain'; email: string };

interface AuthContextValue {
  state: AuthState;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Human-readable sign-in failure (popup/redirect) — shown on the login page. */
  signInError: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setState({ status: 'signed_out' });
        return;
      }
      const email = user.email?.toLowerCase() ?? '';
      const domain = email.split('@')[1];

      if (!ALLOWED_DOMAINS.includes(domain)) {
        // Client-side enforcement: immediately sign out and surface a clear message.
        // Server-side enforcement (rules + functions) is the actual guarantee.
        void fbSignOut(auth);
        setState({ status: 'wrong_domain', email });
        return;
      }
      // Bound the age of an unattended session — force periodic fresh SSO.
      if (sessionExpired(user)) {
        try {
          sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
        } catch {
          /* ignore */
        }
        void fbSignOut(auth);
        setState({ status: 'signed_out' });
        return;
      }
      // A custom-token (impersonation/seed) session persists across tab
      // closes while the per-tab impersonation flag doesn't — re-seed the
      // flag from the token's impersonatedBy claim so the banner + Stop
      // button always render (a seed owner was silently stuck AS their seed).
      void syncImpersonationFromToken();
      setState({ status: 'signed_in', user });
    });
    return unsub;
  }, []);

  // Catch a session that ages out while the app stays open for days.
  useEffect(() => {
    const id = window.setInterval(() => {
      const u = auth.currentUser;
      if (u && sessionExpired(u)) {
        try {
          sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
        } catch {
          /* ignore */
        }
        void fbSignOut(auth);
      }
    }, 60 * 60 * 1000); // hourly
    return () => window.clearInterval(id);
  }, []);

  // Surface errors from a returning REDIRECT sign-in (success is picked up by
  // onAuthStateChanged; this only reports failures, e.g. a misconfigured domain).
  useEffect(() => {
    getRedirectResult(auth).catch((e: unknown) => {
      const code = (e as { code?: string }).code ?? '';
      setSignInError(`Sign-in failed${code ? ` (${code})` : ''}. Please try again.`);
    });
  }, []);

  const signIn = async () => {
    setSignInError(null);
    try {
      if (preferRedirect()) {
        // Full-page navigation to Google and back — reliable on mobile/PWA.
        await signInWithRedirect(auth, googleProvider);
        return;
      }
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged will pick up the new user.
    } catch (e: unknown) {
      const code = (e as { code?: string }).code ?? '';
      if (code === 'auth/popup-closed-by-user') return; // user changed their mind
      if (POPUP_FALLBACK_CODES.has(code)) {
        try {
          await signInWithRedirect(auth, googleProvider);
          return;
        } catch {
          /* fall through to the error message */
        }
      }
      setSignInError(`Sign-in failed${code ? ` (${code})` : ''}. Please try again.`);
    }
  };

  const signOut = async () => {
    await fbSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ state, signIn, signOut, signInError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
